import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Login from './components/auth/Login';
import Layout from './components/layout/Layout';
// Her rota kapısı izni `SAYFA_ROLLERI`nden okur, burada elle rol listesi yazılmaz (rota 3.39).
// Menü ve sayfa içi bağlantılar aynı tabloyu okuyor; `sayfaRolleri.test.ts` bu dosyayı tarar.
import { SAYFA_ROLLERI } from './constants/sayfaRolleri';
import { AuthProvider, useAuthContext } from './contexts/AuthContext';
import ErrorBoundary from './components/ui/ErrorBoundary';

// --- LAZY LOADED ROUTES ---
const Dashboard = React.lazy(() => import('./components/dashboard/Dashboard'));
const UserList = React.lazy(() => import('./components/users/UserList'));
const UserForm = React.lazy(() => import('./components/users/UserForm'));
const ProductList = React.lazy(() => import('./components/products/ProductList'));
const ProductForm = React.lazy(() => import('./components/products/ProductForm'));
const ProductListGorsel = React.lazy(() => import('./components/products/ProductListGorsel'));
const ProductCatalog = React.lazy(() => import('./components/products/ProductCatalog'));
const MaterialList = React.lazy(() => import('./components/materials/MaterialList'));
const OrderList = React.lazy(() => import('./components/orders/OrderList'));
const OrderDetail = React.lazy(() => import('./components/orders/OrderDetail'));
const ProductionBoard = React.lazy(() => import('./pages/ProductionBoard'));
const CustomerList = React.lazy(() => import('./components/customers/CustomerList'));
const CustomerDetail = React.lazy(() => import('./components/customers/CustomerDetail'));
const MatchSuggestions = React.lazy(() => import('./components/customers/MatchSuggestions'));
const FollowUpsPage = React.lazy(() => import('./components/customers/FollowUpsPage'));
const CustomerGroups = React.lazy(() => import('./components/customers/CustomerGroups'));
const Reports = React.lazy(() => import('./pages/Reports'));
const ReportCategory = React.lazy(() => import('./pages/ReportCategory'));
const ReportTeslimatCiro = React.lazy(() => import('./pages/ReportTeslimatCiro'));
const ReportTeslimTutanagi = React.lazy(() => import('./pages/ReportTeslimTutanagi'));
const ReportTerminTeslim = React.lazy(() => import('./pages/ReportTerminTeslim'));
const ReportFinancialRevenue = React.lazy(() => import('./pages/ReportFinancialRevenue'));
const ReportProductPerformance = React.lazy(() => import('./pages/ReportProductPerformance'));
const PaymentTrackingReport = React.lazy(() => import('./pages/PaymentTrackingReport'));
const ReportProvinceHeatmap = React.lazy(() => import('./pages/ReportProvinceHeatmap'));
const IntegrationSettings = React.lazy(() => import('./components/admin/IntegrationSettings'));
const ForgotPassword = React.lazy(() => import('./components/auth/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./components/auth/ResetPassword'));
const UserActivity = React.lazy(() => import('./components/users/UserActivity'));
const MyLoginHistory = React.lazy(() => import('./pages/MyLoginHistory'));
const UserGuide = React.lazy(() => import('./pages/UserGuide/UserGuide'));
const ToplantiListesi = React.lazy(() => import('./components/toplantilar/ToplantiListesi'));
const GorevListesi = React.lazy(() => import('./components/toplantilar/GorevListesi'));
const ToplantiDetay = React.lazy(() => import('./components/toplantilar/ToplantiDetay'));

// Güvenlik Kontrolü + Layout Sarmalayıcı
const ProtectedRoute = ({ children, allowedRoles }: { children: React.ReactNode, allowedRoles?: string[] }) => {
  const { isAuthenticated, user } = useAuthContext();
  const userRole = (user?.rol || '').toLowerCase();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(userRole)) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Layout OLMADAN Güvenlik Kontrolü (Üretim Ekranı için)
const ProtectedRouteNoLayout = ({ children, allowedRoles }: { children: React.ReactNode, allowedRoles?: string[] }) => {
  const { isAuthenticated, user } = useAuthContext();
  const userRole = (user?.rol || '').toLowerCase();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(userRole)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const PageLoader = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
  </div>
);

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      {/* Bildirim kapsayıcısı tek ve kökte: çerçevesiz sayfalar (üretim ekranı) da
          bildirim göstersin. Kapsayıcısı olmayan sayfada gönderilen bildirim kaybolmaz,
          kuyrukta bekler ve kullanıcı kapsayıcılı bir sayfaya geçince orada, bağlamından
          kopuk çıkar. `bildirimVeOnayKurali.test.ts` tek kapsayıcının burada durduğunu tarar. */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <Router>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Davet bağlantısı: aynı sayfa davet metinleriyle, aynı uca gider */}
        <Route path="/davet" element={<ResetPassword tur="davet" />} />

        {/* Dashboard — rol listesi BİLEREK yok: listede olmayan rol '/' yönlendirmesinde döngüye girerdi */}
        <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

        {/* --- ÜRETİM TAKİP EKRANI (Layout YOK - Tam ekran) --- */}
        <Route
          path="/production"
          element={<ProtectedRouteNoLayout allowedRoles={SAYFA_ROLLERI['/production']}><ProductionBoard /></ProtectedRouteNoLayout>}
        />

        {/* --- KULLANICI YÖNETİMİ (Sadece Admin) --- */}
        <Route
          path="/users"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/users']}><UserList /></ProtectedRoute>}
        />
        <Route
          path="/users/new"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/users/new']}><UserForm /></ProtectedRoute>}
        />
        <Route
          path="/users/edit/:id"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/users/edit/:id']}><UserForm /></ProtectedRoute>}
        />
        <Route
          path="/users/:id/activity"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/users/:id/activity']}><UserActivity /></ProtectedRoute>}
        />
        <Route
          path="/me/login-history"
          element={<ProtectedRoute><MyLoginHistory /></ProtectedRoute>}
        />

        {/* --- KULLANIM KILAVUZU (Admin + Operasyon) --- */}
        <Route
          path="/kilavuz"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/kilavuz']}><UserGuide /></ProtectedRoute>}
        />

        {/* --- ENTEGRASYON AYARLARI (Sadece Admin) --- */}
        <Route
          path="/settings/integrations"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/settings/integrations']}><IntegrationSettings /></ProtectedRoute>}
        />

        {/* --- ÜRÜN YÖNETİMİ --- */}
        <Route
          path="/products"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/products']}><ProductList /></ProtectedRoute>}
        />

        {/* Görsel Yönetimi */}
        <Route
          path="/products/images"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/products/images']}><ProductListGorsel /></ProtectedRoute>}
        />

        {/* Ürün Kataloğu: Sadece Admin */}
        <Route
          path="/products/catalog"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/products/catalog']}><ProductCatalog /></ProtectedRoute>}
        />

        {/* Ürün Ekleme: Sadece Admin ve Operasyon */}
        <Route
          path="/products/new"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/products/new']}><ProductForm /></ProtectedRoute>}
        />

        {/* Ürün Düzenleme/Detay */}
        <Route
          path="/products/edit/:id"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/products/edit/:id']}><ProductForm /></ProtectedRoute>}
        />

        {/* --- MALZEME YÖNETİMİ (Reçete & Maliyet Aşama 1) — maliyet modülü: admin + teknik --- */}
        <Route
          path="/materials"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/materials']}><MaterialList /></ProtectedRoute>}
        />

        {/* --- SİPARİŞ YÖNETİMİ --- */}

        {/* 1. Sipariş Listesi */}
        <Route
          path="/orders"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/orders']}><OrderList /></ProtectedRoute>}
        />

        {/* 2. Yeni Manuel Sipariş: Sadece Admin ve Operasyon */}
        <Route
          path="/orders/new"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/orders/new']}><OrderDetail /></ProtectedRoute>}
        />

        {/* 3. Sipariş Detay & Yönetim */}
        <Route
          path="/orders/:id"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/orders/:id']}><OrderDetail /></ProtectedRoute>}
        />

        {/* --- MÜŞTERİ YÖNETİMİ (FAZ 4) --- */}
        <Route
          path="/customers"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/customers']}><CustomerList /></ProtectedRoute>}
        />
        <Route
          path="/customers/new"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/customers/new']}><CustomerDetail /></ProtectedRoute>}
        />
        <Route
          path="/customers/follow-ups"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/customers/follow-ups']}><FollowUpsPage /></ProtectedRoute>}
        />
        <Route
          path="/customers/match-suggestions"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/customers/match-suggestions']}><MatchSuggestions /></ProtectedRoute>}
        />
        <Route
          path="/customers/groups"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/customers/groups']}><CustomerGroups /></ProtectedRoute>}
        />
        <Route
          path="/customers/:id"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/customers/:id']}><CustomerDetail /></ProtectedRoute>}
        />

        {/* --- TOPLANTILAR VE GÖREVLER --- */}
        <Route
          path="/toplantilar"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/toplantilar']}><ToplantiListesi /></ProtectedRoute>}
        />
        <Route
          path="/toplantilar/gorevler"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/toplantilar/gorevler']}><GorevListesi /></ProtectedRoute>}
        />
        <Route
          path="/toplantilar/:id"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/toplantilar/:id']}><ToplantiDetay /></ProtectedRoute>}
        />

        {/* --- RAPORLAMA MODÜLÜ --- */}
        <Route
          path="/reports"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/reports']}><Reports /></ProtectedRoute>}
        />
        <Route
          path="/reports/:categoryId"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/reports/:categoryId']}><ReportCategory /></ProtectedRoute>}
        />
        {/* Teslimat Ciro Raporu */}
        <Route
          path="/reports/operational/teslimat-ciro"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/reports/operational/teslimat-ciro']}><ReportTeslimatCiro /></ProtectedRoute>}
        />
        {/* Teslim Tutanağı (Admin + Operasyon + Teknik) */}
        <Route
          path="/reports/teslim-tutanagi"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/reports/teslim-tutanagi']}><ReportTeslimTutanagi /></ProtectedRoute>}
        />
        {/* Termin/Teslim Performansı Raporu */}
        <Route
          path="/reports/operational/termin-teslim-performansi"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/reports/operational/termin-teslim-performansi']}><ReportTerminTeslim /></ProtectedRoute>}
        />
        {/* Ciro Kırılımı Raporu */}
        <Route
          path="/reports/financial/financial-revenue-breakdown"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/reports/financial/financial-revenue-breakdown']}><ReportFinancialRevenue /></ProtectedRoute>}
        />
        {/* Ürün Performans Raporu */}
        <Route
          path="/reports/product-analysis/product-performance"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/reports/product-analysis/product-performance']}><ReportProductPerformance /></ProtectedRoute>}
        />
        {/* İl Bazlı Isı Haritası (Satış Analizi) */}
        <Route
          path="/reports/sales-analysis/il-bazli-isi-haritasi"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/reports/sales-analysis/il-bazli-isi-haritasi']}><ReportProvinceHeatmap /></ProtectedRoute>}
        />
        {/* Ödeme Takip Raporu (Faz 4.5) */}
        <Route
          path="/reports/payment-tracking"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/reports/payment-tracking']}><PaymentTrackingReport /></ProtectedRoute>}
        />
        {/* Diğer rapor modülleri için placeholder */}
        <Route
          path="/reports/:categoryId/:moduleId"
          element={<ProtectedRoute allowedRoles={SAYFA_ROLLERI['/reports/:categoryId/:moduleId']}><div className="p-6">Rapor modülü yakında...</div></ProtectedRoute>}
        />

        {/* 404 - Sayfa Bulunamadı */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </Router>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;